import {
  CHAT_CHECKOUT_NOTES,
  CHAT_CHECKOUT_NOTE_KINDS,
  CHAT_CHECKOUT_NOTE_LABEL,
  CHAT_CHECKOUT_NOTE_PAX,
  CHAT_CHECKOUT_NOTE_ROOM,
} from "../../constant/chatCheckoutNotes";
import { iChatData } from "../../types/common";
import { voiceBus } from "../../services/voice/voiceBus";
import type { CheckoutIssue } from "../../component/BookingInProgress/context/CheckoutSurfaceContext";

const CHECKOUT_NOTES = new Map<string, string[]>();

const newestPrompt = (chatData: iChatData[]): any[] =>
  [...chatData].reverse().find((entry) => Array.isArray(entry?.prompt) && entry.prompt.length > 0)?.prompt ?? [];

const counted = (count: number, [one, many]: [string, string]) => `${count} ${count === 1 ? one : many}`;

const listed = (parts: string[]) =>
  parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0] ?? "";

export const checkoutKindWord = (kind?: string): string => CHAT_CHECKOUT_NOTE_KINDS[kind ?? ""] ?? CHAT_CHECKOUT_NOTE_KINDS.hotel;

export const checkoutParty = (paxTypes: string[], rooms?: number): string => {
  const people = Object.keys(CHAT_CHECKOUT_NOTE_PAX)
    .map((type) => ({ type, count: paxTypes.filter((pax) => pax === type).length }))
    .filter(({ count }) => count > 0)
    .map(({ type, count }) => counted(count, CHAT_CHECKOUT_NOTE_PAX[type]));
  return rooms ? `${listed(people)} in ${counted(rooms, CHAT_CHECKOUT_NOTE_ROOM)}` : listed(people);
};

export const checkoutTraveler = (index: number, total: number, paxType?: string): string =>
  CHAT_CHECKOUT_NOTES.traveler(index + 1, total, (CHAT_CHECKOUT_NOTE_PAX[paxType ?? ""] ?? CHAT_CHECKOUT_NOTE_PAX.ADT)[0]);

const sentence = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

export const checkoutReason = (message?: string): string => {
  const text = (message ?? "").trim();
  return text ? sentence(text) : CHAT_CHECKOUT_NOTES.documentDate;
};

export const checkoutIssueNote = (issue: CheckoutIssue, message: string): string => {
  if (issue === "rooms") return CHAT_CHECKOUT_NOTES.roomsError(checkoutReason(message));
  const reason = sentence(message.trim());
  return issue === "payFailed" ? CHAT_CHECKOUT_NOTES.payFailed(reason) : CHAT_CHECKOUT_NOTES.payInvalid(reason);
};

export const hasCheckoutNotes = (entryId: string): boolean => (CHECKOUT_NOTES.get(entryId)?.length ?? 0) > 0;

const labelled = (text: string) => `${CHAT_CHECKOUT_NOTE_LABEL} ${text}`;

export const noteChatCheckout = (entryId: string, text: string): void => {
  const note = labelled(text);
  const notes = CHECKOUT_NOTES.get(entryId) ?? [];
  if (notes[notes.length - 1] === note) return;
  CHECKOUT_NOTES.set(entryId, [...notes, note]);
  voiceBus.checkoutNote(note);
};

export const bookedCheckoutNote = (entry?: iChatData): string | null => {
  const booking = entry?.chatBooking;
  if (!booking) return null;
  const kind = checkoutKindWord(booking.kind);
  return labelled(
    booking.status === "inprogress"
      ? CHAT_CHECKOUT_NOTES.inProgress(kind, booking.summary)
      : CHAT_CHECKOUT_NOTES.booked(kind, booking.summary)
  );
};

const entryNotes = (entry: iChatData): string[] => {
  const booked = bookedCheckoutNote(entry);
  const logged = entry?.id ? CHECKOUT_NOTES.get(entry.id) ?? [] : [];
  return booked ? [...logged, booked] : logged;
};

const extendsPrompt = (transcript: any[], prompt: any[]): boolean =>
  JSON.stringify(transcript.slice(0, prompt.length)) === JSON.stringify(prompt);

export const pendingCheckoutNotes = (chatData: iChatData[]): string[] => {
  const transcript = newestPrompt(chatData);
  const used = new Set<number>();
  return chatData.flatMap((entry, index) => {
    const notes = entryNotes(entry);
    if (notes.length === 0) return [];
    const before = newestPrompt(chatData.slice(0, index));
    const from = extendsPrompt(transcript, before) ? before.length : 0;
    return notes.filter((text) => {
      const at = transcript.findIndex(
        (message: any, position: number) => position >= from && !used.has(position) && message?.content === text
      );
      if (at < 0) return true;
      used.add(at);
      return false;
    });
  });
};

export const withCheckoutNotes = (chatData: iChatData[], prior: any[]): any[] => {
  const notes = pendingCheckoutNotes(chatData);
  return notes.length > 0 ? [...prior, ...notes.map((content) => ({ role: "assistant", content }))] : prior;
};
